import cn from 'classnames';
import first from 'lodash/first';
import startCase from 'lodash/startCase';
import { useState, useContext, useEffect, memo } from 'react';

import Heading from 'components/common/Heading';
import Text from 'components/common/Text';
import { UserContext } from 'components/signUpFunnel/Contexts';
import { HOME_CHEF } from 'context/BrandContext';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useBrand from 'hooks/useBrand';
import usePartnershipQuery from 'hooks/usePartnershipQuery';
import { FAMILY, STANDARD, TEMPO } from 'lib/constants';
import { PARTNERSHIP_RESOURCE_TYPES } from 'lib/constants/signUp';
import styles from 'styles/components/signUpFunnel/preferences/MealCardSlider';

import {
  mealCardData,
  GALLERY_BUTTONS,
  STANDARD_GALLERY_BUTTONS,
  PARTNERSHIP_GALLERY_BUTTONS,
} from './mealCardData';

const GalleryButtons = memo(({ activeMealCardType, handleOnClick, galleryButtons }) => (
  <div
    className={cn(
      styles.galleryButtonsContainer,
      'my-2 flex items-center self-stretch px-4 bp1:justify-center'
    )}
  >
    {galleryButtons.map(buttonName => (
      <button
        key={buttonName}
        id={buttonName}
        onClick={() => handleOnClick(buttonName)}
        className={cn(
          styles.galleryButton,
          'textMd bg-cream-110',
          activeMealCardType === buttonName ? styles.active : styles.inactive
        )}
      >
        {startCase(buttonName)}
      </button>
    ))}
  </div>
));

const MealCardSlider = () => {
  const { user } = useContext(UserContext);
  const isHomeChef = useBrand(HOME_CHEF);
  const isTempo = useBrand(TEMPO);
  const { data: partnershipData } = usePartnershipQuery(user?.partnerReferrer);

  const nonPartnershipGalleryButtons = {
    [FAMILY]: [GALLERY_BUTTONS.FAMILY_MEALS],
    [STANDARD]: STANDARD_GALLERY_BUTTONS,
    [TEMPO]: [GALLERY_BUTTONS.TEMPO_MEALS],
  };

  const galleryButtons = partnershipData?.partnership
    ? [
        partnershipData.partnership.name,
        ...(user.mealPlanSlug === FAMILY
          ? [GALLERY_BUTTONS.FAMILY_MEALS]
          : PARTNERSHIP_GALLERY_BUTTONS),
      ]
    : nonPartnershipGalleryButtons[user.mealPlanSlug];

  const [activeMealCardType, setActiveMealCardType] = useState(first(galleryButtons));
  const [animationInitialized, setAnimationInitialized] = useState(false);
  const [mealData, setMealData] = useState({ mealCards: null, mealBadge: null });

  const setMealDataForType = mealCardType => {
    if (partnershipData?.partnership?.name && mealCardType === partnershipData.partnership.name) {
      setMealData({
        mealCards: partnershipData.partnership.resources.filter(
          x => x.resourceType === PARTNERSHIP_RESOURCE_TYPES.MEAL_IMAGE
        ),
        mealBadge: partnershipData.partnership.resources.filter(
          x => x.resourceType === PARTNERSHIP_RESOURCE_TYPES.BADGE
        ),
      });
    } else {
      setMealData({ mealCards: mealCardData[mealCardType], mealBadge: null });
    }
  };

  useEffect(() => {
    setMealDataForType(activeMealCardType);
  }, [partnershipData, activeMealCardType]);

  const handleOnClick = useAnalyticsCallback(
    mealCardType => {
      setActiveMealCardType(mealCardType);
      setAnimationInitialized(true);
      setMealDataForType(mealCardType);
    },
    ({ logEvent, events, callbackArgs }) => {
      logEvent(events.signUp.homechef.preferences[callbackArgs[0]]);
    },
    []
  );

  const ActiveGalleryCards = ({ mealCards, mealBadge }) => {
    return (
      <div className={cn({ [styles.fadeIn]: animationInitialized })}>
        <div className={cn(styles.cardContainer, 'px-4 pb-4')}>
          {mealCards.map((mealCard, index) => (
            <div
              key={`${activeMealCardType}-${index}`}
              className={cn(
                `${styles.card} relative flex h-[18.5rem] w-[12.875rem] flex-col items-start gap-2 rounded-2xl bg-white-100 p-2`,
                {
                  'border-0': !isTempo,
                  'border border-solid border-charcoal-60': isTempo,
                }
              )}
            >
              {mealBadge && mealBadge.length > 0 && (
                <img
                  src={mealBadge[0].url}
                  className="absolute right-0 top-0 z-one"
                  alt="Meal Badge"
                  width="60"
                />
              )}
              <img className="rounded" src={mealCard.url} alt={mealCard.title} />
              <Heading
                brandStylesOverrides="textMd"
                className={cn('pb-2 text-lg leading-tight', { 'font-semibold': isHomeChef })}
              >
                {mealCard.title}
              </Heading>

              <Text brandStylesOverrides="textSm text-secondary">{mealCard.subtitle}</Text>
            </div>
          ))}
        </div>
      </div>
    );
  };
  ActiveGalleryCards.propTypes = {
    mealCards: PropTypes.arrayOf(
      PropTypes.shape({
        badge: PropTypes.string,
        badgeWidth: PropTypes.string,
        subtitle: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      })
    ).isRequired,
    mealBadge: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
      })
    ),
  };

  return (
    <>
      {isHomeChef && galleryButtons.length > 1 && (
        <GalleryButtons
          galleryButtons={galleryButtons}
          activeMealCardType={activeMealCardType}
          handleOnClick={handleOnClick}
        />
      )}
      {mealData.mealCards && (
        <ActiveGalleryCards mealCards={mealData.mealCards} mealBadge={mealData.mealBadge} />
      )}
    </>
  );
};

GalleryButtons.propTypes = {
  galleryButtons: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeMealCardType: PropTypes.string.isRequired,
  handleOnClick: PropTypes.func.isRequired,
};

export default MealCardSlider;
